import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StatusNotice from './ui/StatusNotice';

const AccessLoadingScreen = ({ label = 'Loading your access...', detail = 'Please wait' }) => (
	<div className="min-h-screen bg-black flex items-center justify-center">
		<div className="text-center">
			<div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 mb-4">
				<div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
			</div>
			<div className="text-white text-lg">{label}</div>
			<p className="text-gray-500 text-sm mt-2">{detail}</p>
		</div>
	</div>
);

const AccessDenied = ({ message, backLabel = 'Back to Home' }) => (
	<AccessDeniedContent message={message} backLabel={backLabel} />
);

const AccessDeniedContent = ({ message, backLabel }) => {
	const navigate = useNavigate();

	return (
		<div className="min-h-screen bg-black flex items-center justify-center p-4">
			<StatusNotice
				tone="error"
				title="Access Denied"
				message={message}
				size="lg"
				className="w-full max-w-lg"
				actionLabel={backLabel}
				onAction={() => navigate('/home', { replace: true })}
			/>
		</div>
	);
};

const ProtectedRoute = ({ children, adminOnly = false, requiredAnyPermissions = [] }) => {
	const {
		isAuthenticated,
		isAdmin,
		initializing,
		accessLoading,
		hasAnyPermissions,
	} = useAuth();

	if (initializing || (isAuthenticated && accessLoading)) {
		return <AccessLoadingScreen />;
	}

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	if (adminOnly && !isAdmin) {
		return (
			<AccessDenied message="You don't have administrator permissions to access this page." />
		);
	}

	if (requiredAnyPermissions?.length) {
		const hasPermission = hasAnyPermissions(requiredAnyPermissions);
		if (!hasPermission) {
			return (
				<AccessDenied message="You do not have permission to access this page." />
			);
		}
	}

	return children;
};

export default ProtectedRoute;
